/**
 * Voice AI — Agent Grid
 * Card grid with search, filters, skeleton loading, and empty state.
 * Dispatches: select-agent, create-agent
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

export class AgentGrid extends LitElement {
  static properties = {
    agents: { type: Array },
    loading: { type: Boolean },
    viewMode: { type: String },
    searchQuery: { type: String },
    statusFilter: { type: String },
    ttsProviders: { type: Array },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this.agents = [];
    this.loading = true;
    this.viewMode = localStorage.getItem('voice-agent-view') || 'grid';
    this.searchQuery = '';
    this.statusFilter = '';
    this.ttsProviders = [];
    this._searchDebounce = null;
  }

  _onSearchInput(e) {
    const val = e.target.value;
    clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => {
      this.dispatchEvent(new CustomEvent('search', { detail: { query: val } }));
    }, 300);
  }

  _setStatusFilter(status) {
    this.dispatchEvent(new CustomEvent('filter', { detail: { status } }));
  }

  _selectAgent(agent) {
    this.dispatchEvent(new CustomEvent('select-agent', { detail: { agent } }));
  }

  _createAgent() {
    this.dispatchEvent(new CustomEvent('create-agent'));
  }

  _setViewMode(mode) {
    this.viewMode = mode;
    localStorage.setItem('voice-agent-view', mode);
  }

  render() {
    return html`
      ${iconStyles}
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">AI Agents</h1>
          <p class="page-description">Configure and manage your voice AI agents</p>
        </div>
        <div class="header-actions">
          <div class="view-toggle">
            <button
              class="view-toggle-btn ${this.viewMode === 'grid' ? 'active' : ''}"
              @click=${() => this._setViewMode('grid')}
              title="Grid view"
            >
              <i class="fa-sharp fa-light fa-grid-2"></i>
            </button>
            <button
              class="view-toggle-btn ${this.viewMode === 'list' ? 'active' : ''}"
              @click=${() => this._setViewMode('list')}
              title="List view"
            >
              <i class="fa-sharp fa-light fa-list"></i>
            </button>
          </div>
          <button class="btn btn-primary" @click=${this._createAgent}>
            <i class="fa-sharp fa-light fa-plus"></i>
            Create Agent
          </button>
        </div>
      </div>

      <div class="search-filters">
        <div class="search-input-wrap">
          <i class="fa-sharp fa-light fa-magnifying-glass"></i>
          <input
            type="text"
            class="search-input"
            placeholder="Search agents..."
            .value=${this.searchQuery}
            @input=${this._onSearchInput}
          />
        </div>
        <div class="filter-pills">
          <button class="filter-pill ${this.statusFilter === '' ? 'active' : ''}" @click=${() => this._setStatusFilter('')}>All</button>
          <button class="filter-pill ${this.statusFilter === 'active' ? 'active' : ''}" @click=${() => this._setStatusFilter('active')}>Active</button>
          <button class="filter-pill ${this.statusFilter === 'inactive' ? 'active' : ''}" @click=${() => this._setStatusFilter('inactive')}>Inactive</button>
        </div>
        ${!this.loading ? html`<span class="results-count">${this.agents.length} agent${this.agents.length !== 1 ? 's' : ''}</span>` : ''}
      </div>

      ${this.loading ? html`
        <div class="agents-grid ${this.viewMode === 'list' ? 'list-view' : ''}">
          ${[1, 2, 3].map(() => this.viewMode === 'list'
            ? this._renderSkeletonList()
            : this._renderSkeletonGrid()
          )}
        </div>
      ` : this.agents.length === 0 ? html`
        <div class="empty-state" style="padding:var(--space-16) var(--space-8);">
          <div class="empty-state-icon"><i class="fa-sharp fa-light fa-robot"></i></div>
          <div class="empty-state-title">${this.searchQuery || this.statusFilter ? 'No matching agents' : 'No agents yet'}</div>
          <div class="empty-state-description">
            ${this.searchQuery || this.statusFilter
              ? 'Try adjusting your search or filters.'
              : 'Create your first AI agent to get started.'}
          </div>
          ${!this.searchQuery && !this.statusFilter ? html`
            <button class="btn btn-primary" style="margin-top:var(--space-4);" @click=${this._createAgent}>
              <i class="fa-sharp fa-light fa-plus"></i>
              Create Agent
            </button>
          ` : ''}
        </div>
      ` : html`
        <div class="agents-grid ${this.viewMode === 'list' ? 'list-view' : ''}">
          ${this.agents.map(agent => this._renderCard(agent))}
        </div>
      `}
    `;
  }

  _renderSkeletonGrid() {
    return html`
      <div class="agent-card sk-card">
        <div class="agent-card-header">
          <div class="skeleton-line circle"></div>
          <div class="agent-info">
            <div class="skeleton-line" style="width:65%;height:16px;margin-bottom:8px;"></div>
            <div style="display:flex;gap:var(--space-2);">
              <div class="skeleton-line" style="width:56px;height:20px;border-radius:9999px;"></div>
              <div class="skeleton-line" style="width:32px;height:20px;border-radius:9999px;"></div>
            </div>
          </div>
        </div>
        <div class="agent-stats-row">
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:50%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:70%;"></div>
          </div>
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:45%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:75%;"></div>
          </div>
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:40%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:60%;"></div>
          </div>
        </div>
        <div class="agent-card-footer">
          <div class="skeleton-line" style="width:45%;height:12px;"></div>
          <div class="skeleton-line" style="width:25%;height:12px;"></div>
        </div>
      </div>
    `;
  }

  _renderSkeletonList() {
    return html`
      <div class="agent-card sk-card">
        <div class="agent-card-header" style="margin-bottom:0;">
          <div class="skeleton-line circle"></div>
          <div class="agent-info">
            <div class="skeleton-line" style="width:120px;height:16px;margin-bottom:6px;"></div>
            <div style="display:flex;gap:var(--space-2);">
              <div class="skeleton-line" style="width:56px;height:18px;border-radius:9999px;"></div>
              <div class="skeleton-line" style="width:32px;height:18px;border-radius:9999px;"></div>
            </div>
          </div>
        </div>
        <div class="agent-stats-row" style="margin-bottom:0;">
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:36px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:60px;"></div>
          </div>
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:32px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:70px;"></div>
          </div>
          <div class="agent-stat-item">
            <div class="skeleton-line" style="width:28px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:55px;"></div>
          </div>
        </div>
        <div class="agent-card-footer" style="border-top:none;padding-top:0;">
          <div class="skeleton-line" style="width:100px;height:12px;margin-bottom:4px;"></div>
          <div class="skeleton-line" style="width:70px;height:12px;"></div>
        </div>
      </div>
    `;
  }

  /**
   * Resolve the voice name displayed on the card.
   * If the saved voice_name doesn't match the active TTS provider format,
   * show "Default" since the orchestrator will fall back to the Settings default.
   */
  _getVoiceDisplay(agent) {
    if (!agent.voiceName) return 'Default';

    // Determine the effective TTS provider for this agent
    const providers = this.ttsProviders || [];
    let effectiveProvider = null;
    if (agent.ttsProviderKey) {
      effectiveProvider = providers.find(p => p.key === agent.ttsProviderKey);
    }
    if (!effectiveProvider) {
      effectiveProvider = providers.find(p => p.is_default === 1 || p.is_default === true);
    }
    if (!effectiveProvider) return agent.voiceName;

    const providerType = effectiveProvider.provider;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(agent.voiceName);
    const isDeepgramVoice = /^aura-2-/.test(agent.voiceName);

    // Voice format doesn't match the active provider — orchestrator will use Settings default
    if (providerType === 'cartesia' && !isUuid) return 'Default';
    if (providerType === 'deepgram' && !isDeepgramVoice) return 'Default';
    if (providerType === 'openai' && (isUuid || isDeepgramVoice)) return 'Default';
    if (providerType === 'elevenlabs' && isDeepgramVoice) return 'Default';

    return agent.voiceName;
  }

  _renderCard(agent) {
    return html`
      <div class="agent-card" @click=${() => this._selectAgent(agent)}>
        <div class="agent-card-header">
          <div class="agent-avatar color-${agent.color}">
            <i class="fa-sharp-duotone fa-solid fa-robot"></i>
          </div>
          <div class="agent-info">
            <div class="agent-name">${agent.name}</div>
            <div class="agent-meta">
              <span class="badge ${agent.status === 'active' ? 'badge-success' : 'badge-neutral'}">
                <span class="status-dot ${agent.status === 'active' ? 'active' : 'inactive'}"></span>
                ${agent.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              <span class="badge badge-neutral">${(agent.language || '').toUpperCase()}</span>
            </div>
          </div>
        </div>

        <div class="agent-stats-row">
          <div class="agent-stat-item">
            <span class="stat-value">${agent.totalCalls.toLocaleString()}</span>
            <span class="stat-label">Total Calls</span>
          </div>
          <div class="agent-stat-item">
            <span class="stat-value">${agent.successRate}%</span>
            <span class="stat-label">Success Rate</span>
          </div>
          <div class="agent-stat-item">
            <span class="stat-value">${agent.avgRating}</span>
            <span class="stat-label">Avg Rating</span>
          </div>
        </div>

        <div class="agent-card-footer">
          <span class="last-active">Last active: ${agent.lastActive}</span>
          <span class="voice-name">
            <i class="fa-sharp fa-light fa-waveform-lines" style="font-size: 10px;"></i>
            ${this._getVoiceDisplay(agent)}
          </span>
        </div>
      </div>
    `;
  }
}

customElements.define('agent-grid', AgentGrid);
